import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    IconButton,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    Stack,
    Avatar,
    Chip,
    Alert,
    Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {uploadFile, fetchApi } from '../services/api';



// 시간 포맷 유틸리티 함수
const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? '오후' : '오전';
    const hour12 = hours % 12 || 12;
    return `${ampm} ${hour12}:${minutes}`;
};

// 날짜 포맷 유틸리티 함수
const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayOfWeek = days[date.getDay()];
    return `${year}년 ${month}월 ${day}일 ${dayOfWeek}요일`;
};

// 날짜가 다른지 확인하는 함수
const isDifferentDay = (date1: string, date2: string) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toDateString() !== d2.toDateString();
};

interface CounselingRecord {
    상담기록번호: number;
    등록일시: string;
    내용: string;
    등록자: string;
    등록자번호: number;
    첨부파일: string;
    첨부파일경로: string;
}

interface CounselingRecordDialogProps {
    counselingNo: string;
    open: boolean;
    onClose: () => void;
    onFileAttached?: (file: { fileName: string; filePath: string }) => void;
}

const MAX_FILE_SIZE = 300 * 1024; // 300KB in bytes
const LOCAL_USER = Number(localStorage.getItem('UserNo')) || 0; // 로컬 사용자 번호



const CounselingRecordDialog: React.FC<CounselingRecordDialogProps> = ({
    counselingNo,
    open,
    onClose,
    onFileAttached
}) => {
    

    const [counselingRecords, setCounselingRecords] = useState<CounselingRecord[]>([]);
    const [newRecord, setNewRecord] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textFieldRef = useRef<HTMLTextAreaElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [counselingRecords]);

    // 텍스트 입력 시 높이 자동 조절
    const adjustHeight = () => {
        const textField = textFieldRef.current;
        if (textField) {
            const lineHeight = 20;
            const currentRows = Math.ceil(textField.scrollHeight / lineHeight);
            const adjustedRows = Math.max(3, currentRows - 1); // 최소 3줄, 실제 줄 수보다 1줄 적게

            textField.style.height = 'auto';
            const newHeight = Math.min(adjustedRows * lineHeight, window.innerHeight * 0.5);
            textField.style.height = `${newHeight}px`;
        }
    };

    // 텍스트 변경 핸들러
    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNewRecord(e.target.value);
        setTimeout(adjustHeight, 0);
    };

    // 컴포넌트 마운트 및 newRecord 변경 시 높이 조절
    useEffect(() => {
        adjustHeight();
    }, [newRecord]);

    const fetchRecordsData = async () => {
        if (!counselingNo) return;
        
        setLoading(true);
        try {
            
            const recordsResponse = await fetchApi<{ table: [{ 상담정보: string }] }>('DBO.ITN_S_상담기록', [
                { 컬럼명: '@업체번호', 값: '1' },
                { 컬럼명: '@상담번호', 값: counselingNo },
                { 컬럼명: '@유저번호', 값: '12' }
            ]);
            
            if (recordsResponse.data.table?.[0]?.상담정보) {
                const parsedData = JSON.parse(recordsResponse.data.table[0].상담정보);
                setCounselingRecords(parsedData || []);
            } else {
                setCounselingRecords([]);
            }
        } catch (error) {
            console.error('상담 기록 조회 실패:', error);
            setCounselingRecords([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (counselingNo) {
            fetchRecordsData();
        }
    }, [counselingNo]);

    const handleDownload = (filePath: string) => {
        // 파일 다운로드 로직 구현
        window.open(filePath, '_blank');
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement> | File) => {
        const file = event instanceof File ? event : event.target.files?.[0];
        if (!file) return;

        if (file.size > MAX_FILE_SIZE) {
            setSnackbar({
                open: true,
                message: '파일 크기는 300KB를 초과할 수 없습니다.',
                severity: 'error'
            });
            return;
        }

        setSelectedFile(file);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleFileButtonClick = () => {
        fileInputRef.current?.click();
    };

    let fUrl = '';
    let fName = '';

    const handleSave = async () => {
        if (newRecord.trim() && counselingNo) {
            try {
                // 파일이 첨부된 경우 파일 저장
                if (selectedFile) {
                    const formData = new FormData();
                    formData.append('file', selectedFile);
                    const companyNo = localStorage.getItem('CompanyNo') || '0';
                    formData.append('companyNo', companyNo);

                    const uploadResponse = await uploadFile(formData, 'counseling_fileupload');
                    
                    fUrl = uploadResponse.data.file_url; 
                    fName = fUrl.split('/').pop() || '';

                    await fetchApi('DBO.ITN_T_상담기록저장', [
                        { 컬럼명: '@업체번호', 값: '1' },
                        { 컬럼명: '@상담번호', 값: counselingNo },
                        { 컬럼명: '@상담기록번호', 값: 0 },
                        { 컬럼명: '@내용', 값: newRecord },
                        { 컬럼명: '@처리자', 값: localStorage.getItem('UserNo') },
                        { 컬럼명: '@첨부파일', 값: fName },
                        { 컬럼명: '@첨부파일경로', 값: fUrl }
                    ]);
                } else {
                    await fetchApi('DBO.ITN_T_상담기록저장', [
                        { 컬럼명: '@업체번호', 값: '1' },
                        { 컬럼명: '@상담번호', 값: counselingNo },
                        { 컬럼명: '@상담기록번호', 값: 0 },
                        { 컬럼명: '@내용', 값: newRecord },
                        { 컬럼명: '@처리자', 값: localStorage.getItem('UserNo') },
                        { 컬럼명: '@첨부파일', 값: '' },
                        { 컬럼명: '@첨부파일경로', 값: '' }
                    ]);
                }

                // 즉시 UI 업데이트
                const newMessage = {
                    상담기록번호: Date.now(), // 임시 ID
                    등록일시: new Date().toISOString(),
                    내용: newRecord,
                    등록자: "",
                    등록자번호: Number(LOCAL_USER),
                    첨부파일: selectedFile?.name || '',
                    첨부파일경로: fUrl
                };
                
                setCounselingRecords(prev => [...prev, newMessage]);

                // 파일이 첨부된 경우 부모 컴포넌트에 알림
                if (selectedFile && onFileAttached) {
                    onFileAttached({
                        fileName: fName,
                        filePath: fUrl
                    });
                }

                setNewRecord('');
                setSelectedFile(null);

                // API 응답 후 전체 목록 새로고침
                // await fetchRecordsData();
            } catch (error) {
                console.error('상담 기록 저장 실패:', error);
            }
        }
    };

    if (!open) return null;

    return (
        <Box sx={{ 
            height: '100%', 
            flexDirection: 'column',
            borderLeft: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: counselingNo == '0' ? 'none' : 'flex'
            
        }}>
            {/* <Box sx={{
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center'
            }}>
                <ChatBubbleOutlineIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">상담기록</Typography>
            </Box> */}

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2, backgroundColor: '#BACEE0' }}>
                        {counselingRecords.map((record, index) => {
                            const isLocalUser = record.등록자번호 === Number(LOCAL_USER);
                            const showDate = index === 0 || 
                                (index > 0 && isDifferentDay(record.등록일시, counselingRecords[index - 1].등록일시));

                            return (
                                <React.Fragment key={record.상담기록번호}>
                                    {showDate && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            my: 2 
                                        }}>
                                            <Chip
                                                label={formatDate(record.등록일시)}
                                                sx={{
                                                    bgcolor: '#f5f5f5',
                                                    fontSize: '11px',
                                                    color: '#666'
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: isLocalUser ? 'flex-end' : 'flex-start',
                                            mb: 2,
                                            gap: 1
                                        }}
                                    >
                                        {!isLocalUser && (
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                <PersonIcon />
                                            </Avatar>
                                        )}
                                        <Box sx={{ maxWidth: '70%' }}>
                                            {!isLocalUser ? (
                                                <Typography variant="caption" sx={{ ml: 1, fontSize: '13px' }}>
                                                    {record.등록자}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" sx={{ 
                                                    mr: 1, 
                                                    fontSize: '13px',
                                                    display: 'flex',
                                                    justifyContent: 'flex-end'  // 우측 정렬
                                                }}>
                                                    {record.등록자}
                                                </Typography>
                                            )}
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'flex-end',
                                                flexDirection: isLocalUser ? 'row' : 'row-reverse',
                                                gap: 1,
                                                mb: 0.5,
                                                width: '100%'
                                            }}>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: '#666',
                                                        fontSize: '11px',
                                                        whiteSpace: 'nowrap',
                                                        flex: '0 0 auto'
                                                    }}
                                                >
                                                    {formatTime(record.등록일시)}
                                                </Typography>
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 1.25, 
                                                        bgcolor: isLocalUser ? '#fff3b0' : '#fff',
                                                        borderRadius: 2,
                                                        wordBreak: 'break-word',
                                                        maxWidth: '90%'
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: '13px' }}>
                                                        {record.내용}
                                                    </Typography>
                                                    {record.첨부파일 && (
                                                        <Button
                                                            startIcon={<AttachFileIcon />}
                                                            size="small"
                                                            onClick={() => handleDownload(record.첨부파일경로 || '')}
                                                            sx={{ mt: 0, fontSize: '11px', textTransform: 'none' }}
                                                        >
                                                            {record.첨부파일}
                                                        </Button>
                                                    )}
                                                </Paper>
                                            </Box>
                                        </Box>
                                        {isLocalUser && (
                                            
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                <PersonIcon />
                                            </Avatar>
                                        )}
                                    </Box>
                                </React.Fragment>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </Box>
                )}
                <Box 
                    sx={{ p: 0, borderTop: 0, borderColor: 'divider' }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <TextField
                        fullWidth
                        multiline
                        inputRef={textFieldRef}
                        placeholder="메시지를 입력하세요..."
                        value={newRecord}
                        onChange={handleTextChange}
                        variant="standard"
                        sx={{
                            '& .MuiInputBase-root': {
                                padding: 0,
                                minHeight: '60px',
                                maxHeight: '50vh',
                                lineHeight: 1.5
                            },
                            '& .MuiInputBase-input': {
                                padding: '8px 16px',
                                lineHeight: '20px',
                                overflow: 'auto !important',
                                maxHeight: '50vh'
                            },
                            '& textarea': {
                                height: '100% !important'
                            },
                            '& .MuiInput-underline:before': {
                                borderBottom: 'none'
                            },
                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                borderBottom: 'none'
                            },
                            '& .MuiInput-underline:after': {
                                borderBottom: 'none'
                            }
                        }}
                    />
                </Box>
                <Box 
                    sx={{ p: 1.5 }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    {selectedFile && (
                        <Box sx={{ mt: 0 }}>
                            <Typography variant="caption" color="primary" sx={{ pl: 1 }}>
                                첨부됨: {selectedFile.name}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <Button
                            variant="text"
                            startIcon={<AttachFileIcon />}
                            onClick={handleFileButtonClick}
                            sx={{ 
                                height: '24px',
                                fontSize: '11px',
                                minWidth: 'auto'
                            }}
                        >
                            파일첨부
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleSave}
                            disabled={!newRecord.trim() && !selectedFile}
                            sx={{ 
                                height: '32px',
                                fontSize: '11px'
                            }}
                        >
                            전송
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity="error" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CounselingRecordDialog;
